import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { BiPencil } from 'react-icons/bi';
import { MdDelete } from 'react-icons/md';
import client from '../../api/client';
import { toast } from 'react-toastify';

const EMPTY_FORM = {
  professionalDegreeName: '',
  idProfessionType: '',
};

function AdminQualifications() {
  const [qualifications, setQualifications] = useState([]);
  const [professionTypes, setProfessionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadProfessionTypes = useCallback(async () => {
    try {
      const res = await client.get('/api/professional-types');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setProfessionTypes(res.data.data);
      }
    } catch {
      /* optional */
    }
  }, []);

  const loadQualifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/api/admin/qualifications', {
        params: {
          idProfessionType: departmentFilter || undefined,
        },
      });
      if (res.data?.success) {
        setQualifications(res.data.data || []);
      } else {
        toast.error(res.data?.message || 'Unable to load qualifications.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load qualifications.');
    } finally {
      setLoading(false);
    }
  }, [departmentFilter]);

  useEffect(() => {
    loadProfessionTypes();
  }, [loadProfessionTypes]);

  useEffect(() => {
    loadQualifications();
  }, [loadQualifications]);

  const filteredQualifications = qualifications.filter((q) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return (
      (q.professionalDegreeName || '').toLowerCase().includes(term)
      || (q.professionName || '').toLowerCase().includes(term)
    );
  });

  const openAdd = () => {
    setIsEditMode(false);
    setSelectedQualification(null);
    setFormData({
      professionalDegreeName: '',
      idProfessionType: departmentFilter || '',
    });
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setIsEditMode(true);
    setSelectedQualification(item);
    setFormData({
      professionalDegreeName: item.professionalDegreeName || '',
      idProfessionType: item.idProfessional ? String(item.idProfessional) : '',
    });
    setShowFormModal(true);
  };

  const openDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setSelectedQualification(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.professionalDegreeName.trim();
    const idProfessionType = parseInt(formData.idProfessionType, 10);

    if (!name) {
      toast.error('Qualification name is required.');
      return;
    }
    if (!idProfessionType) {
      toast.error('Please select a department.');
      return;
    }

    const payload = {
      professionalDegreeName: name,
      idProfessionType,
    };

    setIsSubmitting(true);
    try {
      if (isEditMode && selectedQualification) {
        const res = await client.put(
          `/api/admin/qualifications/${selectedQualification.idProfessionalDegree}`,
          payload,
        );
        if (res.data?.success) {
          toast.success('Qualification updated successfully.');
          closeFormModal();
          loadQualifications();
        } else {
          toast.error(res.data?.message || 'Unable to update qualification.');
        }
      } else {
        const res = await client.post('/api/admin/qualifications', payload);
        if (res.data?.success) {
          toast.success('Qualification added successfully.');
          closeFormModal();
          loadQualifications();
        } else {
          toast.error(res.data?.message || 'Unable to add qualification.');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to save qualification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsSubmitting(true);
    try {
      const res = await client.delete(
        `/api/admin/qualifications/${deleteTarget.idProfessionalDegree}`,
      );
      if (res.data?.success) {
        toast.success('Qualification deleted successfully.');
        setShowDeleteModal(false);
        setDeleteTarget(null);
        loadQualifications();
      } else {
        toast.error(res.data?.message || 'Unable to delete qualification.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to delete qualification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Doctor Qualifications</h2>
        <button type="button" className="btn btn-primary" onClick={openAdd}>
          <FaPlus className="me-2" />
          Add Qualification
        </button>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3 mb-md-0">
              <label htmlFor="qualificationSearch" className="form-label">
                Search
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="qualificationSearch"
                  placeholder="Qualification or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label htmlFor="departmentFilter" className="form-label">
                Department
              </label>
              <select
                className="form-select"
                id="departmentFilter"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="">All Departments</option>
                {professionTypes.map((pt) => (
                  <option key={pt.idProfessionType} value={pt.idProfessionType}>
                    {pt.professionTypeName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th scope="col">Sl#</th>
                  <th scope="col">Qualification</th>
                  <th scope="col">Department</th>
                  <th scope="col" className="text-center" style={{ width: '100px' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && filteredQualifications.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No qualifications found
                    </td>
                  </tr>
                )}
                {!loading
                  && filteredQualifications.map((q, idx) => (
                    <tr key={q.idProfessionalDegree}>
                      <td>{idx + 1}</td>
                      <td className="fw-bold">{q.professionalDegreeName}</td>
                      <td>{q.professionName || '—'}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-1"
                          title="Edit"
                          onClick={() => openEdit(q)}
                        >
                          <BiPencil />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => openDelete(q)}
                        >
                          <MdDelete />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Qualification' : 'Add Qualification'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <label htmlFor="qualificationName" className="form-label">
                Qualification Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="qualificationName"
                placeholder="e.g. MBBS, MD, MS, DNB"
                maxLength={50}
                value={formData.professionalDegreeName}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  professionalDegreeName: e.target.value,
                }))}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="qualificationDepartment" className="form-label">
                Department <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="qualificationDepartment"
                value={formData.idProfessionType}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  idProfessionType: e.target.value,
                }))}
                required
              >
                <option value="">Select department</option>
                {professionTypes.map((pt) => (
                  <option key={pt.idProfessionType} value={pt.idProfessionType}>
                    {pt.professionTypeName}
                  </option>
                ))}
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Add'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Qualification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete
          {' '}
          <strong>{deleteTarget?.professionalDegreeName}</strong>
          {' '}
          from
          {' '}
          <strong>{deleteTarget?.professionName}</strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminQualifications;
